import React, { useState } from 'react';
import CameraComponent from './components/native/CameraComponent';
import BiometricAuthComponent from './components/native/BiometricAuthComponent';
import OfflineStorageComponent from './components/native/OfflineStorageComponent';
import { formatNyDateTime } from './utils/datetime';

const NativeTestPage = ({ user, setShowNativeTest }) => {
  const [currentTest, setCurrentTest] = useState('overview');
  const [authResult, setAuthResult] = useState(null);
  const [capturedPhotos, setCapturedPhotos] = useState([]);

  const handlePhotoCapture = (photo) => {
    setCapturedPhotos(prev => [...prev, { ...photo, timestamp: new Date().toISOString() }]);
    console.log('Photo captured:', photo);
  };

  const handleAuthSuccess = (result) => {
    setAuthResult({ success: true, result, timestamp: new Date().toISOString() });
    console.log('Biometric authentication successful:', result);
  };

  const handleAuthFailure = (error) => {
    setAuthResult({ success: false, error, timestamp: new Date().toISOString() });
    console.log('Biometric authentication failed:', error);
  };

  const testSections = {
    overview: { title: '📱 Visão Geral', icon: '📱' },
    camera: { title: '📷 Câmera', icon: '📷' },
    biometric: { title: '🔐 Biometria', icon: '🔐' },
    storage: { title: '💾 Storage Offline', icon: '💾' }
  };

  return (
    <div className="native-test-page min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">
              🚀 Teste de Recursos Nativos iOS
            </h1>
            <button
              onClick={() => setShowNativeTest(false)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              ← Voltar ao App
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Testando integração React → iOS com Capacitor
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {Object.entries(testSections).map(([key, section]) => (
            <button
              key={key}
              onClick={() => setCurrentTest(key)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-colors ${
                currentTest === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {section.icon} {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        {currentTest === 'overview' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">📱 Visão Geral dos Recursos Nativos</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">✅ Recursos Implementados</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• 📷 Captura de fotos (Câmera + Galeria)</li>
                    <li>• 🔐 Autenticação biométrica (Face ID/Touch ID)</li>
                    <li>• 💾 Armazenamento offline (SQLite)</li>
                    <li>• 🔄 Sincronização de dados</li>
                    <li>• 📱 Interface nativa iOS</li>
                  </ul>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">📊 Status Atual</h3>
                  <div className="text-sm text-green-700 space-y-1">
                    <div>📷 Fotos capturadas: {capturedPhotos.length}</div>
                    <div>🔐 Última autenticação: {authResult ? (authResult.success ? '✅ Sucesso' : '❌ Falha') : 'Não testada'}</div>
                    <div>👤 Usuário: {user?.username || 'N/A'}</div>
                    <div>📱 Plataforma: iOS/Capacitor</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h3 className="font-semibold text-yellow-800 mb-2">🚀 Próximos Recursos</h3>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• 🔔 Push Notifications (Firebase)</li>
                    <li>• 🌐 Sincronização em background</li>
                    <li>• 📊 Analytics e crash reporting</li>
                    <li>• 🎨 Tema nativo iOS</li>
                    <li>• 🏪 Preparação App Store</li>
                  </ul>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-semibold text-purple-800 mb-2">🧪 Como Testar</h3>
                  <ol className="text-sm text-purple-700 space-y-1">
                    <li>1. Use as abas acima para testar cada recurso</li>
                    <li>2. Teste no simulador iOS ou device real</li>
                    <li>3. Verifique permissões iOS</li>
                    <li>4. Teste offline/online scenarios</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentTest === 'camera' && (
          <div className="bg-white rounded-lg shadow-sm">
            <CameraComponent onPhotoCapture={handlePhotoCapture} />
            
            {capturedPhotos.length > 0 && (
              <div className="p-4 border-t">
                <h4 className="text-md font-medium mb-3">📸 Histórico de Fotos ({capturedPhotos.length})</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {capturedPhotos.slice().reverse().map((photo, index) => (
                    <div key={index} className="relative">
                      <img 
                        src={photo.dataUrl} 
                        alt={`Captured ${index + 1}`} 
                        className="w-full h-24 object-cover rounded border"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1">
                        {new Date(photo.timestamp).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {currentTest === 'biometric' && (
          <div className="bg-white rounded-lg shadow-sm">
            <BiometricAuthComponent 
              onAuthSuccess={handleAuthSuccess}
              onAuthFailure={handleAuthFailure}
            />
            
            {authResult && (
              <div className="p-4 border-t">
                <h4 className="text-md font-medium mb-3">📋 Resultado da Autenticação</h4>
                <div className={`p-3 rounded-lg ${
                  authResult.success ? 'bg-green-50' : 'bg-red-50'
                }`}>
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">
                      {authResult.success ? '✅' : '❌'}
                    </span>
                    <div>
                      <div className={`font-medium ${
                        authResult.success ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {authResult.success ? 'Autenticação Bem-sucedida' : 'Falha na Autenticação'}
                      </div>
                      <div className={`text-sm ${
                        authResult.success ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {authResult.success ? 'Acesso liberado' : authResult.error}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatNyDateTime(authResult.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentTest === 'storage' && (
          <div className="bg-white rounded-lg shadow-sm">
            <OfflineStorageComponent currentUser={user} />
          </div>
        )}
      </div>
    </div>
  );
};

export default NativeTestPage;